import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { transferApi } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { ArrowRightLeft, DollarSign, Coins, User, Lock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Transfer() {
  const [activeTab, setActiveTab] = useState<'nt' | 'ms'>('nt');
  const [receiverAccount, setReceiverAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [msAmount, setMsAmount] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const handleNTTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receiverAccount || !amount || !password) {
      toast.error('請填寫所有必填欄位');
      return;
    }
    setLoading(true);
    try {
      await transferApi.transferNT({
        receiverAccount,
        amount: parseFloat(amount),
        reason: reason || undefined,
        password,
      });
      toast.success('轉帳成功！');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.error || '轉帳失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleMSTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receiverAccount || !msAmount || !password) {
      toast.error('請填寫所有必填欄位');
      return;
    }
    setLoading(true);
    try {
      await transferApi.transferMsDirect({
        receiverAccount,
        msAmount,
        reason: reason || undefined,
        password,
      });
      toast.success('數資幣轉帳成功！');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.error || '轉帳失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900">轉帳</h1>
        <p className="text-slate-500 mt-2">安全快速地轉帳給其他用戶</p>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => setActiveTab('nt')}
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 font-medium transition-all ${
              activeTab === 'nt'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            新臺幣
          </button>
          <button
            onClick={() => setActiveTab('ms')}
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 font-medium transition-all ${
              activeTab === 'ms'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Coins className="h-5 w-5" />
            數資幣
          </button>
        </div>

        {/* Balance Display */}
        <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100">
          <p className="text-sm text-slate-500 mb-1">目前餘額</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">
              {activeTab === 'nt' ? 
                `$${user?.ntBalance?.toLocaleString() || 0}` : 
                `${user?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 0} MS∮`
              }
            </span>
          </div>
        </div>

        {/* Form */}
        <div className="p-6">
          {activeTab === 'nt' ? (
            <form onSubmit={handleNTTransfer} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">收款人帳號</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={receiverAccount}
                    onChange={(e) => setReceiverAccount(e.target.value)}
                    placeholder="請輸入對方帳號"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">轉帳金額 (NTD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</span>
                  <input
                    type="number"
                    className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="請輸入金額"
                    min="1"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">轉帳原因 (選填)</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="例如：午餐費、還款等"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">確認密碼</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="請輸入您的密碼確認"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    處理中...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="h-5 w-5" />
                    確認轉帳
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMSTransfer} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">收款人帳號</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    value={receiverAccount}
                    onChange={(e) => setReceiverAccount(e.target.value)}
                    placeholder="請輸入對方帳號"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">轉帳金額 (MS∮)</label>
                <div className="relative">
                  <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    value={msAmount}
                    onChange={(e) => setMsAmount(e.target.value)}
                    placeholder="請輸入數資幣金額"
                    required
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">直接輸入整數，例如：1000000</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">轉帳原因 (選填)</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="例如：遊戲點數、獎勵等"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">確認密碼</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="請輸入您的密碼確認"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    處理中...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="h-5 w-5" />
                    確認轉帳
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
